import { useEffect, useSyncExternalStore } from 'react';

/** One open object (BB-20): a segment, an event, an attribute… shown as a tab in its environment. */
export interface ObjectTab {
  type: string;
  id: string;
  title: string;
  /** Dashboard path of the object, and of its list (where closing the open tab goes). */
  url: string;
  listUrl: string;
}

export const MAX_TABS = 10;
const key = (environmentId: string) => `bb.tabs.${environmentId}`;
const listeners = new Set<() => void>();
const cache = new Map<string, ObjectTab[]>();

/** Tabs live in `localStorage` per environment; a blocked or broken storage only loses them on reload. */
function read(environmentId: string): ObjectTab[] {
  const cached = cache.get(environmentId);
  if (cached) return cached;
  let tabs: ObjectTab[] = [];
  try {
    const raw = window.localStorage.getItem(key(environmentId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) tabs = parsed.filter((t): t is ObjectTab => Boolean(t && typeof t.type === 'string' && typeof t.id === 'string' && typeof t.url === 'string'));
  } catch {
    tabs = [];
  }
  cache.set(environmentId, tabs);
  return tabs;
}

function write(environmentId: string, tabs: ObjectTab[]) {
  cache.set(environmentId, tabs);
  try {
    window.localStorage.setItem(key(environmentId), JSON.stringify(tabs));
  } catch {
    // Private mode or a full quota: the tabs still work for this page view.
  }
  for (const listener of listeners) listener();
}

export const objectTabs = {
  list: read,
  open(environmentId: string, tab: ObjectTab) {
    const tabs = read(environmentId);
    const existing = tabs.findIndex((t) => t.type === tab.type && t.id === tab.id);
    if (existing >= 0) {
      if (tabs[existing]!.title === tab.title && tabs[existing]!.url === tab.url) return;
      write(environmentId, tabs.map((t, i) => (i === existing ? tab : t)));
    } else write(environmentId, [...tabs, tab].slice(-MAX_TABS));
  },
  close(environmentId: string, type: string, id: string) {
    write(environmentId, read(environmentId).filter((t) => !(t.type === type && t.id === id)));
  },
  /** For tests: forget what was read. */
  reset() {
    cache.clear();
  },
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useObjectTabs(environmentId: string): ObjectTab[] {
  return useSyncExternalStore(
    objectTabs.subscribe,
    () => read(environmentId),
    () => read(environmentId),
  );
}

/** Registers the object an editor page shows as an open tab (and keeps its title current). */
export function useObjectTab(environmentId: string, tab: ObjectTab | null) {
  const signature = tab ? `${tab.type}|${tab.id}|${tab.title}|${tab.url}|${tab.listUrl}` : '';
  useEffect(() => {
    if (tab) objectTabs.open(environmentId, tab);
    // `signature` stands for `tab`: a new object literal every render must not reopen the tab.
  }, [environmentId, signature]);
}
